import type { BulkImport, BulkImportStatus } from '@/types/bulkImport'
import { ApiErrorException } from '@/types/apiErrors'

export const BULK_IMPORT_POLL_INTERVAL_MS = 3_000

export const BULK_IMPORT_POLL_MAX_MS = 600_000

const MAX_BACKOFF_MS = 30_000

export function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve()
      return
    }
    const onAbort = () => {
      clearTimeout(timer)
      resolve()
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

export class BulkImportPollError extends Error {
  constructor(public readonly kind: 'timeout' | 'cancelled') {
    super(`BULK_IMPORT_POLL_${kind.toUpperCase()}`)
    this.name = 'BulkImportPollError'
  }
}

export function isBulkImportPollError(e: unknown): e is BulkImportPollError {
  return e instanceof BulkImportPollError
}

export interface BulkImportPollDeps {
  fetchStatus: () => Promise<BulkImport>
  intervalMs?: number
  maxMs?: number
  signal?: AbortSignal
}

export interface BulkImportPollTick {
  import?: BulkImport
  terminal: boolean
  rateLimited: boolean
}

export async function pollBulkImportOnce(
  fetchStatus: () => Promise<BulkImport>,
  terminalStatuses: BulkImportStatus[],
): Promise<BulkImportPollTick> {
  try {
    const imp = await fetchStatus()
    return { import: imp, terminal: terminalStatuses.includes(imp.status), rateLimited: false }
  } catch (err) {
    if (err instanceof ApiErrorException && err.getStatus() === 429) {
      return { terminal: false, rateLimited: true }
    }
    throw err
  }
}

// Terminal statuses like 'invalid' and 'failed' are NOT thrown, the caller inspects them.
export async function pollBulkImportUntil(
  terminalStatuses: BulkImportStatus[],
  deps: BulkImportPollDeps,
): Promise<BulkImport> {
  const intervalMs = deps.intervalMs ?? BULK_IMPORT_POLL_INTERVAL_MS
  const maxMs = deps.maxMs ?? BULK_IMPORT_POLL_MAX_MS
  const { signal } = deps
  const deadline = Date.now() + maxMs

  let consecutiveRateLimits = 0

  while (Date.now() < deadline) {
    if (signal?.aborted) throw new BulkImportPollError('cancelled')
    const tick = await pollBulkImportOnce(deps.fetchStatus, terminalStatuses)
    if (signal?.aborted) throw new BulkImportPollError('cancelled')
    if (tick.rateLimited) {
      consecutiveRateLimits++
      const backoff = Math.min(intervalMs * Math.pow(2, consecutiveRateLimits - 1), MAX_BACKOFF_MS)
      await delay(backoff, signal)
      continue
    }
    consecutiveRateLimits = 0
    if (tick.terminal) return tick.import as BulkImport
    await delay(intervalMs, signal)
  }

  throw new BulkImportPollError('timeout')
}
